// src/AuthContext.tsx
import 'aws-amplify/auth/enable-oauth-listener';

import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import * as Sentry from '@sentry/react';
import React, { useState, useEffect, useContext, createContext } from 'react';
import { fetchAuthSession, signInWithRedirect, signOut as awsSignOut } from 'aws-amplify/auth';
import { ACCOUNT_TYPE } from './constants/authConstants';
import { isStaging } from './globalSettings';
import NoAccountMessage from './Components/NoAccountMessage';
import type { DprBaseline, Role } from 'src/types/user';

interface AuthContextType {
  user: CurrentUser;
  refresh(): Promise<void>;
  accessToken: string | undefined;
}

export interface CurrentUser {
  email: string;
  role: Role;
  firstName: string;
  lastName: string;
  accountId: string;
  accountType: string;
  accountName: string;
  picture: string;
  hasMultipleAccounts: boolean;
  isInternal: boolean;
  isRetailRep: boolean;
  isMultifamily: boolean;
  isInteriorDesigner: boolean;
  isDev: boolean;
  canTransferProjects?: boolean;
}

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: 'us-east-2_U70DWMuzs',
      userPoolClientId: '5mnml7kraj6lrk6j4o3nkp00l3',
      signUpVerificationMethod: 'code',
      loginWith: {
        oauth: {
          domain: 'login.designtool.modularclosets-apps.com',
          scopes: ['email', 'profile', 'openid'],
          redirectSignIn: [window.location.origin],
          redirectSignOut: [window.location.origin],
          responseType: 'code',
        },
        username: true,
        email: true,
        phone: false,
      },
    },
  },
});

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<CurrentUser | undefined>(undefined);
  const userRef = React.useRef<CurrentUser | undefined>(undefined);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const isEmbedded = window.self !== window.top;
  useEffect(() => {
    const key = 'dprBaseline';
    function getTrustedDpr(liveDpr: number): number {
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      const stored = sessionStorage.getItem(key);
      const baseline: DprBaseline | null = stored ? JSON.parse(stored) : null;
      if (
        baseline &&
        baseline.screenWidth === screenWidth &&
        baseline.screenHeight === screenHeight &&
        baseline.dpr !== liveDpr
      ) {
        return baseline.dpr;
      }
      const value: DprBaseline = {
        dpr: liveDpr,
        screenHeight,
        screenWidth,
      };
      sessionStorage.setItem(key, JSON.stringify(value));
      return liveDpr;
    }

    const dpr = window.devicePixelRatio;
    const trustedDpr = getTrustedDpr(dpr);
    console.warn(`DPR: ${dpr} (trusted: ${trustedDpr})`);
  }, []);
  useEffect(() => {
    const unlisten = Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'customOAuthState') {
        if (payload.data === 'close-popup') {
          await updateUserAndToken(true);
          window.close();
          return;
        }
        const returnUrl = payload.data;
        localStorage.setItem('returnUrl', returnUrl);
        if (typeof returnUrl === 'string') {
          window.location.assign(returnUrl);
        }
      }
    });
    return () => {
      unlisten();
    };
  }, []);

  const updateUserAndToken = async (broadcast = false) => {
    const currentUser = await getUserFromJwt();
    const token = await getAccessToken();

    setUser(currentUser);
    setAccessToken(token);

    if (currentUser) {
      Sentry.setUser({
        email: currentUser.email,
        id: currentUser.email,
        username: `${currentUser.firstName} ${currentUser.lastName}`.trim(),
        accountId: currentUser.accountId,
        accountName: currentUser.accountName,
        accountType: currentUser.accountType,
        role: currentUser.role,
        isDev: currentUser.isDev,
      });
    } else {
      Sentry.setUser(null);
    }

    if (currentUser && broadcast) {
      const channel = new BroadcastChannel('mc-auth');
      channel.postMessage('logged-in');
      channel.close();
    }
  };

  const refresh = async () => {
    await fetchAuthSession({ forceRefresh: true });
    await updateUserAndToken();
  };

  useEffect(() => {
    const initializeUserSession = async () => {
      try {
        await updateUserAndToken();
      } catch {
        setUser(undefined);
        setAccessToken(undefined);
        Sentry.setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    initializeUserSession();
  }, []);
  // 2b. Listen for cross-window login ()
  useEffect(() => {
    const channel = new BroadcastChannel('mc-auth');
    channel.onmessage = async (e) => {
      if (e.data === 'logged-in') {
        if (userRef.current) return;
        await refresh();
      } else if (e.data === 'logged-out') {
        if (!userRef.current) return;
        await refresh();
      }
    };
    return () => {
      channel.close();
    };
  }, []);

  const signIn = () => {
    const currentUrl = window.location.href;
    const target = `${window.location.origin}/login`;
    const w = 720;
    const h = 720;
    const dualLeft = window.screenLeft ?? window.screenX ?? 0;
    const dualTop = window.screenTop ?? window.screenY ?? 0;
    const width = window.innerWidth || document.documentElement.clientWidth || screen.width;
    const height = window.innerHeight || document.documentElement.clientHeight || screen.height;
    const left = dualLeft + Math.max(0, (width - w) / 2);
    const top = dualTop + Math.max(0, (height - h) / 2);
    const features = `scrollbars=yes,resizable=yes,width=${w},height=${h},top=${top},left=${left}`;

    const win = window.open(target, 'mc-auth', features);
    if (!win) {
      if (isEmbedded) setPopupBlocked(true);
      else signInWithRedirect({ customState: currentUrl });
    }
  };

  if (window.location.pathname === '/login') {
    signInWithRedirect({ customState: 'close-popup' });
    return <div />;
  }

  if (window.location.pathname === '/login-page' && !user) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <button onClick={() => signIn()}>Sign In</button>
      </div>
    );
  } else if (window.location.pathname === '/login-page') {
    window.location.href = '/';
    return <div />;
  }
  if (!isLoading && !user) {
    if (isEmbedded) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Sign in to view projects.</p>
          <button onClick={signIn}>Sign In</button>
          {popupBlocked && <p>Allow popups for this site, then try again.</p>}
        </div>
      );
    }
    signInWithRedirect({ customState: window.location.href });
  }
  return (
    <AuthContext.Provider value={{ user: user as CurrentUser, refresh, accessToken }}>
      {isLoading ? (
        <div />
      ) : user ? (
        !user.accountId || user.accountId === '' || user.accountId === 'null' ? (
          <NoAccountMessage />
        ) : (
          children
        )
      ) : (
        <div />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export async function getAccessToken() {
  try {
    const localStorageToken = localStorage.getItem('overrideAccessToken');
    if (localStorageToken) {
      return localStorageToken;
    }
    const currentSession = await fetchAuthSession();
    const { accessToken } = currentSession.tokens ?? {};
    return accessToken?.toString();
  } catch (err) {
    console.error('Failed to get access token:', err);
    return undefined;
  }
}

export async function getUserFromJwt(): Promise<CurrentUser | undefined> {
  try {
    const local = getUserFromLocalstorageJwtToken(localStorage.getItem('overrideAccessToken'));
    if (local) {
      return local;
    }

    const currentSession = await fetchAuthSession();
    const { accessToken, idToken } = currentSession.tokens ?? {};
    if (!accessToken?.payload || !idToken?.payload) {
      return undefined;
    }

    return parseJwtPayload(
      accessToken.payload,
      typeof idToken.payload.picture === 'string' ? idToken.payload.picture : '',
    );
  } catch {
    return undefined;
  }
}

export function getUserFromLocalstorageJwtToken(
  token: string | null | undefined,
): CurrentUser | undefined {
  if (!token) {
    return undefined;
  }
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) {
      return undefined;
    }
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    const payload: Record<string, unknown> = JSON.parse(jsonPayload);
    return parseJwtPayload(payload);
  } catch {
    return undefined;
  }
}

function parseJwtPayload(payload: Record<string, unknown>, picture: string = ''): CurrentUser {
  const splitAndTrim = (str?: string): string[] =>
    (str ?? '')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);

  const pickBy = (arr: string[]): string => {
    const match = arr.find((x) =>
      isStaging ? x.toLowerCase().startsWith('s-') : !x.toLowerCase().startsWith('s-'),
    );
    return (match ?? '').replace(/^s-/i, '');
  };

  const pickField = (field: unknown): string =>
    pickBy(splitAndTrim(typeof field === 'string' ? field : undefined));

  const email = typeof payload.email === 'string' ? payload.email : '';
  const isHexecoderEmail = email.toLowerCase().endsWith('@hexacoder.com');
  const accountType = pickField(payload.account_type);
  const isDev = pickField(payload.is_dev);
  const emailsAllowedToTransferProjects = [
    'jennifert@modularclosets.com',
    'jeff@modularclosets.com',
    'joe@modularclosets.com',
    'spacefindersfl@gmail.com',
  ];
  const canTransferProjects =
    emailsAllowedToTransferProjects.includes(email.toLowerCase()) ||
    isDev === 'true' ||
    isHexecoderEmail;

  return {
    email,
    role: pickField(payload.role) as Role,
    firstName: typeof payload.given_name === 'string' ? payload.given_name : '',
    lastName: typeof payload.family_name === 'string' ? payload.family_name : '',
    accountId: pickField(payload.account_id),
    accountType,
    accountName: pickField(payload.account_name),
    picture,
    hasMultipleAccounts: pickField(payload.has_multiple_accounts) === 'true' || isHexecoderEmail,
    isRetailRep: accountType === ACCOUNT_TYPE.RETAIL_REP,
    isMultifamily: accountType === ACCOUNT_TYPE.MULTIFAMILY,
    isInteriorDesigner: accountType === ACCOUNT_TYPE.INTERIOR_DESIGN,
    isInternal: accountType === ACCOUNT_TYPE.RETAIL_REP || accountType === ACCOUNT_TYPE.MULTIFAMILY,
    isDev: isDev === 'true' || isHexecoderEmail,
    canTransferProjects,
  };
}

export async function signOut() {
  try {
    await awsSignOut();
  } catch (error) {
    console.error('Error signing out', error);
  } finally {
    localStorage.clear();
    Sentry.setUser(null);
  }
}
